import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Pencil, Plus, AlertCircle, BarChart4 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { useData } from "@/hooks/useData";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatNumber } from "@/utils/numberFormatter";
import { FuelModalForm } from "./FuelModalForm";
import { FuelEntry, FuelPayload, FuelSourceDoc, FuelSourceDocWithId } from "@/types/fuel";
import {
  calculateFuelCarbonFootprint,
  calculateFuelHydricFootprint,
  calculateTotalFuelCarbonFootprint,
  calculateTotalFuelHydricFootprint,
} from "../../utils/calculations/annualConsumptions";

// Empty fuel entry for new entries
const emptyEntry: FuelEntry = {
  fuelSource: "",
  quantity: "",
  unit: "L",
  pc_comb: 0,
  ph_comb: 0,
};

interface FuelsStepProps {
  existingData?: FuelPayload;
  onDataChange: (payload: FuelPayload) => void;
  showTitle?: boolean;
  checkboxId?: string;
}

export const FuelsStep = ({ existingData, onDataChange, showTitle = true, checkboxId = "noFuelConsumption" }: FuelsStepProps) => {
  const { t } = useLanguage();

  // Initialize entries array from existingData.entries, if provided
  const [fuelEntries, setFuelEntries] = useState<FuelEntry[]>(
    Array.isArray(existingData?.entries) ? existingData.entries : []
  );
  const [noFuelConsumption, setNoFuelConsumption] = useState<boolean>(
    existingData?.noFuelConsumption || false
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formEntry, setFormEntry] = useState<FuelEntry>(emptyEntry);

  // Fetch fuel source docs
  const { data: fuelSources } = useData<FuelSourceDocWithId>({
    collectionName: "setup_fuels",
  });

  // Get fuel source options not already selected
  const sourceOptions = (fuelSources || [])
    .map((doc) => ({ value: doc.name, label: doc.name }))
    .filter((opt) =>
      // Only filter if multiple sources are defined in setup
      fuelSources && fuelSources.length > 1 ? !fuelEntries.some((e) => e.fuelSource === opt.value) : true
    );

  // Sync up if existingData changes
  useEffect(() => {
    const entries = Array.isArray(existingData?.entries) ? existingData.entries : [];
    setFuelEntries(entries);
    setNoFuelConsumption(existingData?.noFuelConsumption || false);
  }, [existingData]);

  /** Find fuel source document by name */
  const getSourceDoc = (sourceName: string): FuelSourceDoc | undefined => {
    return (fuelSources || []).find((doc) => doc.name === sourceName);
  };

  /** Get available units for a specific fuel source */
  const getAvailableUnitsForSource = (sourceName: string): string[] => {
    const sourceDoc = getSourceDoc(sourceName);
    return sourceDoc?.units || ["GJ"];
  };

  // Recalculate footprints when fuelSources are loaded or changed
  useEffect(() => {
    if (fuelSources && fuelSources.length > 0 && fuelEntries.length > 0) {
      let hasChanges = false;

      const updatedEntries = fuelEntries.map(entry => {
        // Skip entries without quantity
        if (!entry.quantity || Number(entry.quantity) === 0) {
          return entry;
        }

        const sourceDoc = getSourceDoc(entry.fuelSource);
        if (!sourceDoc) {
          return entry;
        }

        // Always recalculate using current fuel source data
        const carbonResult = calculateFuelCarbonFootprint(entry, sourceDoc);
        const hydricResult = calculateFuelHydricFootprint(entry, sourceDoc);

        // Check if values changed
        if (
          entry.pc_comb !== carbonResult.footprint ||
          entry.ph_comb !== hydricResult.footprint ||
          entry.carbonFactor !== carbonResult.factor ||
          entry.hydricFactor !== hydricResult.factor ||
          entry.pci !== carbonResult.pci ||
          entry.density !== carbonResult.density
        ) {
          hasChanges = true;
          return {
            ...entry,
            pc_comb: carbonResult.footprint,
            ph_comb: hydricResult.footprint,
            carbonFactor: carbonResult.factor,
            hydricFactor: hydricResult.factor,
            pci: carbonResult.pci,
            density: carbonResult.density,
          };
        }

        return entry;
      });

      // Only update if something actually changed
      if (hasChanges) {
        setFuelEntries(updatedEntries);
        onDataChange({
          entries: updatedEntries,
          total_pc_comb: calculateTotalFuelCarbonFootprint(updatedEntries),
          total_ph_comb: calculateTotalFuelHydricFootprint(updatedEntries),
          noFuelConsumption,
        });
      }
    }
  }, [fuelSources, fuelEntries, getSourceDoc, onDataChange, noFuelConsumption]);

  /** Handle checkbox for no fuel consumption */
  const handleNoFuelConsumptionChange = (checked: boolean) => {
    setNoFuelConsumption(checked);
    if (checked) {
      // Clear all entries when checkbox is checked
      setFuelEntries([]);
      onDataChange({
        entries: [],
        total_pc_comb: 0,
        total_ph_comb: 0,
        noFuelConsumption: true,
      });
    } else {
      onDataChange({
        entries: fuelEntries,
        total_pc_comb: calculateTotalFuelCarbonFootprint(fuelEntries),
        total_ph_comb: calculateTotalFuelHydricFootprint(fuelEntries),
        noFuelConsumption: false,
      });
    }
  };

  /** Open form modal (add vs edit) */
  const openModal = (entry?: FuelEntry, idx?: number) => {
    if (entry && typeof idx === "number") {
      setFormEntry({
        ...entry,
        fuelSource: entry.fuelSource,
      });
      setEditingIndex(idx);
    } else {
      setFormEntry(emptyEntry);
      setEditingIndex(null);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setFormEntry(emptyEntry);
      setEditingIndex(null);
    }, 100);
  };

  /** Save (add/edit) */
  const handleSave = () => {
    const sourceDoc = getSourceDoc(formEntry.fuelSource);

    // Calculate footprints and get factors used
    const carbonResult = calculateFuelCarbonFootprint(formEntry, sourceDoc);
    const hydricResult = calculateFuelHydricFootprint(formEntry, sourceDoc);

    const entryWithFootprints: FuelEntry = {
      ...formEntry,
      pc_comb: carbonResult.footprint,
      ph_comb: hydricResult.footprint,
      carbonFactor: carbonResult.factor,
      hydricFactor: hydricResult.factor,
      pci: carbonResult.pci,
      density: carbonResult.density,
    };

    const newEntries = [...fuelEntries];
    if (editingIndex !== null) newEntries[editingIndex] = entryWithFootprints;
    else newEntries.push(entryWithFootprints);

    setFuelEntries(newEntries);
    onDataChange({
      entries: newEntries,
      total_pc_comb: calculateTotalFuelCarbonFootprint(newEntries),
      total_ph_comb: calculateTotalFuelHydricFootprint(newEntries),
      noFuelConsumption,
    });
    closeModal();
  };

  /** Remove one entry */
  const handleRemoveEntry = (index: number) => {
    const newEntries = fuelEntries.filter((_, i) => i !== index);
    setFuelEntries(newEntries);
    onDataChange({
      entries: newEntries,
      total_pc_comb: calculateTotalFuelCarbonFootprint(newEntries),
      total_ph_comb: calculateTotalFuelHydricFootprint(newEntries),
      noFuelConsumption,
    });
  };

  const handleFormEntryChange = (entry: FuelEntry) => {
    // Set unit based on fuel source if available
    if (entry.fuelSource && !entry.unit) {
      const units = getAvailableUnitsForSource(entry.fuelSource);
      if (units.length > 0) {
        entry.unit = units[0];
      } else {
        entry.unit = "GJ";
      }
    }
    setFormEntry(entry);
  };

  return (
    <div className="space-y-6">
      {/* Title + Add button */}
      {showTitle ? (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t("footprints.categories.fuels.title")}</h2>
            <p className="text-sm text-gray-500 mt-1">{t("footprints.guidelines.fuels")}</p>
          </div>
          {sourceOptions.length === 0 ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-blue-700 text-sm mb-4">
              {t("fuels.noOptionsAvailable")}
            </div>
          ) : (
            <div className="mb-4">
              <Button
                onClick={() => openModal()}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                disabled={sourceOptions.length === 0 || noFuelConsumption}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("fuels.addFuel")}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex justify-end mb-4">
          {sourceOptions.length === 0 ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-blue-700 text-sm mb-4">
              {t("fuels.noOptionsAvailable")}
            </div>
          ) : (
            <div className="mb-4">
              <Button
                onClick={() => openModal()}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                disabled={sourceOptions.length === 0 || noFuelConsumption}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("fuels.addFuel")}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* No Fuel Consumption Checkbox - only show when there are no entries */}
      {fuelEntries.length === 0 && (
        <div className="flex items-center space-x-2 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <Checkbox
            id={checkboxId}
            checked={noFuelConsumption}
            onCheckedChange={handleNoFuelConsumptionChange}
          />
          <label
            htmlFor={checkboxId}
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
          >
            {t("fuels.noFuelConsumption")}
          </label>
        </div>
      )}

      {/* No entries / list of cards */}
      {fuelEntries.length === 0 ? (
        <Alert variant="default" className="bg-gray-50 border-gray-200">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-gray-400 mr-2" />
            <AlertDescription className="text-gray-500">{t("fuels.noEntries")}</AlertDescription>
          </div>
        </Alert>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {fuelEntries.map((entry, idx) => (
              <Card key={idx} className="flex flex-col h-full shadow-sm hover:shadow transition-shadow">
                <CardHeader className="bg-gray-50 pb-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg font-semibold">{entry.fuelSource}</CardTitle>
                    <div className="flex space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openModal(entry, idx)}>
                        <Pencil className="h-4 w-4 text-gray-500 hover:text-teal-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRemoveEntry(idx)}>
                        <Trash2 className="h-4 w-4 text-gray-500 hover:text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="py-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-0 pb-4">
                      <span className="text-sm text-gray-500">{t("common.quantity")}:</span>
                      <span>
                        {formatNumber(Number(entry.quantity))} {entry.unit}
                      </span>
                    </div>
                    {entry.density === undefined ||
                      (entry.density === null && (
                        <div className="text-xs text-yellow-600 italic mt-1">{t("fuels.defaultDensityApplied")}</div>
                      ))}
                    {entry.pci === undefined ||
                      (entry.pci === null && (
                        <div className="text-xs text-yellow-600 italic mt-1">{t("fuels.defaultPciApplied")}</div>
                      ))}
                  </div>
                </CardContent>

                <div className="mt-auto">
                  <div className="bg-teal-50/80 border-t py-3 px-4">
                    <div className="flex justify-between items-center text-teal-700 w-full">
                      <div className="flex items-center gap-1.5">
                        <BarChart4 className="h-5 w-5 align-middle" />
                        <span className="align-middle">{t("common.carbonFootprint")}</span>
                      </div>
                      <span className="text-lg font-bold">
                        {formatNumber(entry.pc_comb)}{" "}
                        <span className="text-sm font-normal">
                          {t("dashboard.company.kgCO2")}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div className="bg-cyan-50/50 border-t py-3 px-4">
                    <div className="flex justify-between items-center text-cyan-600 w-full">
                      <div className="flex items-center gap-1.5">
                        <BarChart4 className="h-5 w-5 align-middle" />
                        <span className="align-middle">{t("common.hydricFootprint")}</span>
                      </div>
                      <span className="text-lg font-bold">
                        {formatNumber(entry.ph_comb)}{" "}
                        <span className="text-sm font-normal">
                          m<sup>3</sup>
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-teal-100 bg-white p-6">
              <div className="flex items-center gap-2 text-teal-700 mb-1">
                <BarChart4 className="h-5 w-5" />
                <span className="text-lg font-medium">{t("common.totalCarbonFootprint")}</span>
              </div>

              <p className="text-xs italic text-teal-600 ml-7 mb-4">{t("footprints.carbonFootprintFuelsFormula")}</p>

              <div className="flex justify-end">
                <span className="text-xl font-bold text-teal-700">
                  {formatNumber(calculateTotalFuelCarbonFootprint(fuelEntries))}{" "}
                  <span>
                    {t("dashboard.company.kgCO2")}
                  </span>
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-cyan-100 bg-white p-6">
              <div className="flex items-center gap-2 text-cyan-600 mb-1">
                <BarChart4 className="h-5 w-5" />
                <span className="text-lg font-medium">{t("common.totalHydricFootprint")}</span>
              </div>

              <p className="text-xs italic text-cyan-600 ml-7 mb-4">{t("footprints.hydricFootprintFuelsFormula")}</p>

              <div className="flex justify-end">
                <span className="text-xl font-bold text-cyan-600">
                  {formatNumber(calculateTotalFuelHydricFootprint(fuelEntries))}{" "}
                  <span>
                    m<sup>3</sup>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <FuelModalForm
          onClose={closeModal}
          onSave={handleSave}
          formEntry={formEntry}
          onFormEntryChange={handleFormEntryChange}
          sourceOptions={sourceOptions}
          editing={editingIndex !== null}
          availableUnits={formEntry.fuelSource ? getAvailableUnitsForSource(formEntry.fuelSource) : []}
        />
      </Dialog>
    </div>
  );
};

export type { FuelPayload };
